import React, { useEffect, useState } from "react";
import { Ruler } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";

const SHAPES = ["Round", "Square", "Rectangle", "Cat Eye", "Aviator", "Oval", "Geometric", "Browline", "Oversized"];
const STYLES = ["Minimal", "Classic", "Bold", "Luxury", "Retro", "Contemporary"];
const CONDITIONS = [
  { label: "10 — Like New", value: 10 },
  { label: "9+ Excellent", value: 9 },
  { label: "8+ Very Good", value: 8 },
  { label: "7+ Good", value: 7 },
];
const DISCOUNTS = [
  { label: "10%+ Off", value: 10 },
  { label: "30%+ Off", value: 30 },
  { label: "50%+ Off", value: 50 },
  { label: "70%+ Off", value: 70 },
];
const FIT_TOLERANCE = 3;

function FilterGroup({ title, children }) {
  return (
    <div className="border-b border-zinc-100 pb-5 mb-5">
      <div className="font-heading text-xs uppercase tracking-wide mb-3 text-teal-700">{title}</div>
      {children}
    </div>
  );
}

function CheckRow({ checked, onChange, label, count, testId }) {
  return (
    <label className="flex items-center justify-between py-1 text-sm cursor-pointer text-zinc-700" data-testid={testId}>
      <span className="flex items-center gap-2">
        <input type="checkbox" checked={checked} onChange={onChange} className="accent-teal-600" />
        {label}
      </span>
      {count !== undefined && <span className="text-xs text-zinc-400">{count}</span>}
    </label>
  );
}

export function FilterSidebar({ filters, setFilters, brands = [], meta = {} }) {
  const { user } = useAuth();
  const [mySizes, setMySizes] = useState([]);

  useEffect(() => {
    if (!user) return;
    api.get("/account/measurements").then((res) => setMySizes(res.data)).catch(() => {});
  }, [user]);

  const toggle = (key, value) => {
    setFilters((prev) => {
      const list = prev[key] ? prev[key].split(",") : [];
      const next = list.includes(value) ? list.filter((v) => v !== value) : [...list, value];
      return { ...prev, [key]: next.join(",") };
    });
  };

  const applyMySize = (m) => {
    setFilters((prev) => ({
      ...prev,
      lens_width_min: m.lens_width - FIT_TOLERANCE,
      lens_width_max: m.lens_width + FIT_TOLERANCE,
      bridge_width_min: m.bridge_width - FIT_TOLERANCE,
      bridge_width_max: m.bridge_width + FIT_TOLERANCE,
      temple_length_min: m.temple_length - FIT_TOLERANCE,
      temple_length_max: m.temple_length + FIT_TOLERANCE,
    }));
  };

  return (
    <div className="w-full lg:w-64 shrink-0" data-testid="filter-sidebar">
      {mySizes.length > 0 && (
        <FilterGroup title="Find My Size">
          {mySizes.map((m) => (
            <button
              key={m.id}
              onClick={() => applyMySize(m)}
              className="flex items-center gap-2 w-full text-left text-sm py-1.5 text-teal-700 hover:text-teal-900"
              data-testid={`filter-use-my-size-${m.id}`}
            >
              <Ruler size={14} /> {m.label} ({m.lens_width}–{m.bridge_width}–{m.temple_length})
            </button>
          ))}
        </FilterGroup>
      )}

      <FilterGroup title="Brand">
        {brands.map((b) => (
          <CheckRow
            key={b.slug}
            checked={(filters.brand || "").split(",").includes(b.slug)}
            onChange={() => toggle("brand", b.slug)}
            label={b.name}
            count={b.product_count}
            testId={`filter-brand-${b.slug}`}
          />
        ))}
      </FilterGroup>

      <FilterGroup title="Condition">
        {CONDITIONS.map((c) => (
          <label key={c.value} className="flex items-center gap-2 py-1 text-sm cursor-pointer text-zinc-700" data-testid={`filter-condition-${c.value}`}>
            <input
              type="radio"
              name="condition_min"
              checked={String(filters.condition_min || "") === String(c.value)}
              onChange={() => setFilters((prev) => ({ ...prev, condition_min: c.value }))}
              className="accent-teal-600"
            />
            {c.label}
          </label>
        ))}
        {filters.condition_min && (
          <button className="text-xs underline text-zinc-400 mt-1" onClick={() => setFilters((p) => ({ ...p, condition_min: "" }))}>
            Clear
          </button>
        )}
      </FilterGroup>

      <FilterGroup title="Price">
        <div className="flex items-center gap-2">
          <input
            type="number"
            placeholder="Min"
            value={filters.min_price || ""}
            onChange={(e) => setFilters((p) => ({ ...p, min_price: e.target.value }))}
            className="w-full bg-white border border-zinc-200 px-2 py-1.5 text-sm text-zinc-900 placeholder:text-zinc-400 rounded-md focus:border-teal-600 outline-none"
            data-testid="filter-min-price"
          />
          <span className="text-zinc-400">–</span>
          <input
            type="number"
            placeholder="Max"
            value={filters.max_price || ""}
            onChange={(e) => setFilters((p) => ({ ...p, max_price: e.target.value }))}
            className="w-full bg-white border border-zinc-200 px-2 py-1.5 text-sm text-zinc-900 placeholder:text-zinc-400 rounded-md focus:border-teal-600 outline-none"
            data-testid="filter-max-price"
          />
        </div>
      </FilterGroup>

      <FilterGroup title="Shape">
        {(meta.shapes || SHAPES).map((s) => (
          <CheckRow key={s} checked={(filters.shape || "").split(",").includes(s)} onChange={() => toggle("shape", s)} label={s} testId={`filter-shape-${s}`} />
        ))}
      </FilterGroup>

      <FilterGroup title="Style">
        {(meta.styles || STYLES).map((s) => (
          <CheckRow key={s} checked={(filters.style || "").split(",").includes(s)} onChange={() => toggle("style", s)} label={s} testId={`filter-style-${s}`} />
        ))}
      </FilterGroup>

      <FilterGroup title="Gender">
        {["Men's", "Women's", "Unisex"].map((s) => (
          <CheckRow key={s} checked={(filters.gender || "").split(",").includes(s)} onChange={() => toggle("gender", s)} label={s} testId={`filter-gender-${s}`} />
        ))}
      </FilterGroup>

      {meta.materials?.length > 0 && (
        <FilterGroup title="Material">
          {meta.materials.map((m) => (
            <CheckRow key={m} checked={(filters.material || "").split(",").includes(m)} onChange={() => toggle("material", m)} label={m} testId={`filter-material-${m}`} />
          ))}
        </FilterGroup>
      )}

      {meta.colours?.length > 0 && (
        <FilterGroup title="Colour">
          {meta.colours.map((c) => (
            <CheckRow key={c} checked={(filters.colour || "").split(",").includes(c)} onChange={() => toggle("colour", c)} label={c} testId={`filter-colour-${c}`} />
          ))}
        </FilterGroup>
      )}

      <FilterGroup title="Discount">
        {DISCOUNTS.map((d) => (
          <label key={d.value} className="flex items-center gap-2 py-1 text-sm cursor-pointer text-zinc-700" data-testid={`filter-discount-${d.value}`}>
            <input
              type="radio"
              name="discount_min"
              checked={String(filters.discount_min || "") === String(d.value)}
              onChange={() => setFilters((prev) => ({ ...prev, discount_min: d.value }))}
              className="accent-teal-600"
            />
            {d.label}
          </label>
        ))}
        {filters.discount_min && (
          <button className="text-xs underline text-zinc-400 mt-1" onClick={() => setFilters((p) => ({ ...p, discount_min: "" }))}>
            Clear
          </button>
        )}
      </FilterGroup>

      <FilterGroup title="Measurements (mm)">
        <div className="space-y-3">
          {[
            { key: "lens_width", label: "Lens Width", range: meta.lens_width },
            { key: "bridge_width", label: "Bridge Width", range: meta.bridge_width },
            { key: "temple_length", label: "Temple Length", range: meta.temple_length },
          ].map((f) => (
            <div key={f.key}>
              <div className="text-xs text-zinc-500 mb-1">{f.label}{f.range ? ` (${f.range.min}–${f.range.max})` : ""}</div>
              <div className="flex items-center gap-2">
                <input
                  type="number"
                  placeholder="Min"
                  value={filters[`${f.key}_min`] || ""}
                  onChange={(e) => setFilters((p) => ({ ...p, [`${f.key}_min`]: e.target.value }))}
                  className="w-full bg-white border border-zinc-200 px-2 py-1.5 text-sm text-zinc-900 placeholder:text-zinc-400 rounded-md focus:border-teal-600 outline-none"
                  data-testid={`filter-${f.key}-min`}
                />
                <span className="text-zinc-400">–</span>
                <input
                  type="number"
                  placeholder="Max"
                  value={filters[`${f.key}_max`] || ""}
                  onChange={(e) => setFilters((p) => ({ ...p, [`${f.key}_max`]: e.target.value }))}
                  className="w-full bg-white border border-zinc-200 px-2 py-1.5 text-sm text-zinc-900 placeholder:text-zinc-400 rounded-md focus:border-teal-600 outline-none"
                  data-testid={`filter-${f.key}-max`}
                />
              </div>
            </div>
          ))}
        </div>
      </FilterGroup>
    </div>
  );
}
